import { useState, useEffect, useMemo, useId } from 'react'
import { Link } from '@tanstack/react-router'
import FocusLock from 'react-focus-lock'
import { Wifi as WifiIcon, BatteryCharging as ChargingIcon, Pencil as PencilIcon, X as XIcon, Clock as ClockIcon, Monitor as MonitorIcon } from 'lucide-react'
import { api } from '../lib/api'
import { formatDate, getDeviceStatus, getiPhoneModelName, resolveLastSeen } from '../lib/utils'
import { useActiveJobs } from '../context/ActiveJobsContext'
import { useDevice, useDevicePolicies, useDeviceStatus } from '../hooks/useDeviceQuery'
import { usePolicies } from '../hooks/usePolicyQuery'
import { useQueryClient } from '@tanstack/react-query'
import { useSubscription } from '../context/SubscriptionContext'
import { queries } from '../lib/queries'
import { polling } from '../lib/polling'
import { useIsMobile } from '../hooks/useIsMobile'
import { DeviceOperationModal } from './device-operation'
import { DeviceReadinessPanel } from './DeviceReadinessPanel'

interface DeviceModalProps {
  deviceId: string
  onClose: () => void
}

export default function DeviceModal({ deviceId, onClose }: DeviceModalProps) {
  const queryClient = useQueryClient()
  const dialogTitleId = useId()

  const { canPerformPaidActions, isLoading: subscriptionLoading } = useSubscription()
  const showSubscriptionRestriction = !subscriptionLoading && !canPerformPaidActions

  const isMobile = useIsMobile()

  const { data: device, isLoading: loading, error: deviceError, refetch: refetchDevice } = useDevice(deviceId, {
    refetchInterval: polling.deviceStatus,
  })
  const { data: deviceStatus } = useDeviceStatus(deviceId, {
    refetchInterval: polling.deviceStatus,
  })
  const error = deviceError?.message || null

  const { addJob, activeJobs, setSelectedJobId } = useActiveJobs()

  const activeJobForDevice = activeJobs.find(
    (job) => job.deviceId === deviceId || (device?.nanomdmDeviceId && job.udid === device.nanomdmDeviceId)
  )

  const [actionError, setActionError] = useState<string | null>(null)
  const [isStartingJob, setIsStartingJob] = useState(false)

  const { data: policiesData, refetch: refetchPolicies } = useDevicePolicies(deviceId, {
    refetchInterval: polling.deviceStatus,
  })
  const policies = policiesData || null

  const { data: availablePoliciesData } = usePolicies()
  const availablePolicies = useMemo(() => availablePoliciesData?.policies || [], [availablePoliciesData])

  const [selectedPolicyId, setSelectedPolicyId] = useState<string>('')
  const [applyingPolicy, setApplyingPolicy] = useState(false)
  const [removingPolicy, setRemovingPolicy] = useState<string | null>(null)

  const [showUninstallModal, setShowUninstallModal] = useState(false)

  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [savingName, setSavingName] = useState(false)

  useEffect(() => {
    if (availablePolicies.length > 0 && !selectedPolicyId) {
      setSelectedPolicyId(availablePolicies[0].id)
    }
  }, [availablePolicies, selectedPolicyId])

  useEffect(() => {
    if (!policies) return
    if (applyingPolicy && selectedPolicyId) {
      const appliedAssignment = policies.assignments.find(
        (a) => a.policy.id === selectedPolicyId && a.status === 'applied'
      )
      if (appliedAssignment) {
        setApplyingPolicy(false)
      }
    }
    if (removingPolicy !== null) {
      const stillApplied = policies.assignments.some(
        (a) => a.policy.id === removingPolicy && a.status === 'applied'
      )
      if (!stillApplied) {
        setRemovingPolicy(null)
      }
    }
  }, [policies, removingPolicy, applyingPolicy, selectedPolicyId])

  const statusLastSeen = resolveLastSeen(
    deviceStatus?.nanomdmStatus?.lastSeen,
    device?.nanomdm?.lastSeenAt,
    device?.lastSeen,
  )
  const statusIsEnrolled = deviceStatus?.isEnrolled ?? device?.isEnrolled ?? false
  const status = device ? getDeviceStatus(statusLastSeen, statusIsEnrolled) : 'offline'
  const isOnline = status === 'online'
  const isConnected = device?.isConnected === true

  const handleSupervise = async () => {
    if (!device) return
    try {
      setActionError(null)
      setIsStartingJob(true)
      const response = await api.superviseDevice(device.id)
      addJob({
        commandId: response.commandId,
        type: 'supervise',
        deviceId: device.id,
        deviceName: device.name || undefined,
        udid: device.nanomdmDeviceId,
        createdAt: new Date().toISOString(),
        currentStep: 'progress',
      })
      setSelectedJobId(response.commandId)
      onClose()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to initiate supervision')
      setIsStartingJob(false)
    }
  }

  const handleUninstallClick = () => {
    setShowUninstallModal(true)
  }

  const handleViewJobProgress = () => {
    if (activeJobForDevice) {
      setSelectedJobId(activeJobForDevice.commandId)
      onClose()
    }
  }

  const handleApplyPolicy = async () => {
    if (!selectedPolicyId || !device) return
    try {
      setApplyingPolicy(true)
      setActionError(null)
      await api.applyPolicy(device.id, selectedPolicyId)
      await refetchPolicies()
      queryClient.invalidateQueries({ queryKey: queries.devices.list() })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to apply rule')
      setApplyingPolicy(false)
    }
  }

  const handleRemovePolicy = async (policyId: string) => {
    if (!device) return
    try {
      setRemovingPolicy(policyId)
      setActionError(null)
      await api.removePolicy(device.id, policyId)
      await refetchPolicies()
      queryClient.invalidateQueries({ queryKey: queries.devices.list() })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove rule')
      setRemovingPolicy(null)
    }
  }

  const activePolicies = policies?.assignments.filter((a) => a.status === 'applied' || a.status === 'pending') || []
  const hasActivePolicy = activePolicies.length > 0

  const handleStartEditName = () => {
    setEditedName(device?.name || '')
    setIsEditingName(true)
  }

  const handleSaveName = async () => {
    if (!device) return
    const trimmedName = editedName.trim()
    if (trimmedName === (device.name || '')) {
      setIsEditingName(false)
      return
    }
    setSavingName(true)
    try {
      await api.updateDevice(device.id, { name: trimmedName || undefined })
      await refetchDevice()
      setIsEditingName(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update device name')
    } finally {
      setSavingName(false)
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName()
    } else if (e.key === 'Escape') {
      setIsEditingName(false)
    }
  }

  const getDeviceType = () => {
    if (!device) return ''
    if (device.nanomdm?.model) {
      return device.nanomdm.model
    }
    return device.platform === 'ios' ? 'iOS Device' : 'Android Device'
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showUninstallModal) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, showUninstallModal])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center modal-backdrop bg-black/30"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
    >
      <FocusLock returnFocus>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-4xl w-full sm:mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="p-4 sm:p-6 motion-safe:animate-pulse" aria-busy="true">
            <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/3 mb-3"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 sm:p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
              <h3 className="text-sm sm:text-base font-medium text-red-800 mb-1.5">Error</h3>
              <p className="text-xs sm:text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-3 py-1.5 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors active:bg-gray-300"
            >
              Close
            </button>
          </div>
        ) : device ? (
          <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            {/* Header with Close Button */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {isEditingName ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={handleSaveName}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Add a name for this device"
                      autoFocus
                      disabled={savingName}
                      className="text-lg sm:text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-brand outline-none w-full max-w-[300px] placeholder:text-gray-400 placeholder:font-normal"
                    />
                  ) : (
                    <button
                      onClick={handleStartEditName}
                      className="group flex items-center gap-2 text-left hover:bg-gray-50 rounded-lg -ml-2 px-2 py-1 transition-colors"
                    >
                      <h1 id={dialogTitleId} className={`text-lg sm:text-2xl font-bold truncate ${device.name ? 'text-gray-900' : 'text-gray-400'}`}>
                        {device.name || 'Add device name'}
                      </h1>
                      <PencilIcon size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  )}
                  <div className="flex sm:hidden items-center gap-1.5 shrink-0">
                    {isOnline && (
                      <>
                        <WifiIcon size={16} className="text-status-online" />
                        <span className="text-xs font-medium text-status-online">Online</span>
                      </>
                    )}
                    {!isOnline && isConnected && (
                      <>
                        <ChargingIcon size={16} className="text-blue-600" />
                        <span className="text-xs font-medium text-blue-600">Connected</span>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-base text-gray-500">{getDeviceType()}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="hidden sm:flex items-center gap-2">
                  {isOnline && (
                    <>
                      <WifiIcon size={16} className="text-status-online" />
                      <span className="text-sm font-medium text-status-online">Online</span>
                    </>
                  )}
                  {!isOnline && isConnected && (
                    <>
                      <ChargingIcon size={16} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">Connected</span>
                    </>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <XIcon size={18} />
                </button>
              </div>
            </div>

            {/* Device Info Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">Model</p>
                <p className="text-xs sm:text-sm text-gray-900 leading-tight">
                  {(() => {
                    const model = device.detectedDevice?.model || device.nanomdm?.model
                    if (model) {
                      return getiPhoneModelName(model)
                    }
                    return device.platform === 'ios' ? 'iPhone' : 'Android Device'
                  })()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">OS Version</p>
                <p className="text-xs sm:text-sm text-gray-900">
                  {device.detectedDevice?.osVersion || device.nanomdm?.osVersion || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">Last Seen</p>
                <p className="text-xs sm:text-sm text-gray-900">{formatDate(statusLastSeen) || 'Never'}</p>
              </div>
              {device.nanomdm?.serialNumber && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Serial Number</p>
                  <p className="text-gray-900 font-mono text-xs break-all">{device.nanomdm.serialNumber}</p>
                </div>
              )}
            </div>

            {/* Error message */}
            {actionError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs sm:text-sm text-red-700">{actionError}</p>
              </div>
            )}

            {/* Policies Section */}
            <div className="border-t border-gray-200 pt-3 sm:pt-4">
              <h2 className="text-base font-semibold text-gray-900 mb-2 sm:mb-3">Rules</h2>
              {device.hasActiveSchedule ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <ClockIcon size={16} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Managed by Schedule</span>
                  </div>
                  <p className="text-xs text-amber-700 mb-2">
                    Rules are automatically applied by &quot;{device.activeScheduleName}&quot;
                  </p>
                  <Link
                    to="/schedules"
                    className="text-xs text-amber-700 underline hover:text-amber-900"
                  >
                    View Schedules &rarr;
                  </Link>
                </div>
              ) : hasActivePolicy ? (
                <div className="space-y-1.5">
                  {activePolicies.map((assignment) => (
                    <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="text-xs sm:text-sm text-gray-900 truncate">{assignment.policy.name}</span>
                        {assignment.status === 'pending' && (
                          <span className="text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded shrink-0">
                            Applying...
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemovePolicy(assignment.policy.id)}
                        disabled={removingPolicy === assignment.policy.id || assignment.status === 'pending'}
                        className="px-2 py-1 text-xs sm:text-sm text-red-600 hover:bg-red-50 transition-colors rounded disabled:opacity-50 ml-2 shrink-0"
                      >
                        {removingPolicy === assignment.policy.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : device.isEnrolled && availablePolicies.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2">
                    <select
                      value={selectedPolicyId}
                      onChange={(e) => setSelectedPolicyId(e.target.value)}
                      disabled={showSubscriptionRestriction}
                      className="flex-1 px-2 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus-visible:ring-2 focus-visible:ring-brand/50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {availablePolicies.map((policy) => (
                        <option key={policy.id} value={policy.id}>
                          {policy.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleApplyPolicy}
                      disabled={applyingPolicy || !selectedPolicyId || showSubscriptionRestriction}
                      className="px-3 py-1.5 text-xs sm:text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={showSubscriptionRestriction ? 'Active subscription required' : undefined}
                    >
                      {applyingPolicy ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                  {showSubscriptionRestriction && (
                    <p className="text-xs text-amber-600">
                      Reactivate subscription to apply rules
                    </p>
                  )}
                </div>
              ) : !device.isEnrolled ? (
                <p className="text-xs sm:text-sm text-gray-500">
                  Unbreakable must be installed to apply rules
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-gray-500">
                  No rules available
                </p>
              )}
            </div>

            {/* Unbreakable Section */}
            <div className="border-t border-gray-200 pt-3 sm:pt-4">
              {activeJobForDevice ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="relative">
                      <div className="w-3 h-3 bg-brand rounded-full motion-safe:animate-pulse" />
                      <div className="absolute inset-0 w-3 h-3 bg-brand rounded-full motion-safe:animate-ping opacity-75" />
                    </div>
                    <span className="text-sm text-brand font-medium">
                      {activeJobForDevice.type === 'supervise' ? 'Installing...' : 'Uninstalling...'}
                    </span>
                  </div>
                  <button
                    onClick={handleViewJobProgress}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors"
                  >
                    View Progress
                  </button>
                </div>
              ) : (device.isSupervised || device.isDeviceOwner) ? (
                isMobile ? (
                  <div className="w-full px-3 py-1.5 text-xs sm:text-sm text-red-600 border border-red-200 rounded-lg bg-red-50 flex items-center gap-2">
                    <MonitorIcon size={16} className="text-red-600 shrink-0" />
                    <span className="text-xs text-red-600">You can only uninstall on desktop</span>
                  </div>
                ) : (
                  <DeviceReadinessPanel
                    platform={device.platform as 'ios' | 'android' | null}
                    deviceName={device.name}
                    deviceConnected={isConnected}
                    mode="uninstall"
                    onAction={handleUninstallClick}
                  />
                )
              ) : (
                <div className="flex flex-col gap-2">
                  <DeviceReadinessPanel
                    platform={device.platform as 'ios' | 'android' | null}
                    deviceName={device.name}
                    deviceConnected={isConnected}
                    mode="install"
                    onAction={handleSupervise}
                    actionDisabled={isStartingJob || showSubscriptionRestriction}
                    actionLabel={isStartingJob ? 'Starting...' : 'Install Unbreakable'}
                  />
                  {showSubscriptionRestriction && (
                    <p className="text-xs text-amber-600">
                      Reactivate subscription to install
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      </FocusLock>

      {/* Uninstall Modal */}
      {showUninstallModal && device && (
        <DeviceOperationModal
          mode="uninstall"
          deviceId={device.id}
          deviceName={device.name || undefined}
          devicePlatform={device.platform as 'ios' | 'android'}
          onClose={() => setShowUninstallModal(false)}
          onComplete={() => {
            setShowUninstallModal(false)
            onClose()
          }}
        />
      )}
    </div>
  )
}
